import { useEffect } from 'react';

// Ref-counted across consumers so overlapping lockers (a sheet with a modal opened on top) lock the body once and restore it only after the last one releases.
let lockCount = 0;

const LOCKED_PROPERTIES = ['overflow', 'position', 'width', 'top'] as const;

type BodyLockStyle = Record<(typeof LOCKED_PROPERTIES)[number], string>;

let savedBody: (BodyLockStyle & { scrollY: number }) | null = null;
let ownBody: BodyLockStyle | null = null;

const readBodyLockStyle = (): BodyLockStyle => ({
  overflow: document.body.style.overflow,
  position: document.body.style.position,
  width: document.body.style.width,
  top: document.body.style.top,
});

/**
 * Locks body scroll while `active`; safe when multiple lockers overlap.
 */
const useOverflowHidden = (active: boolean): void => {
  useEffect(() => {
    if (!active) {
      return;
    }

    if (lockCount === 0) {
      savedBody = { ...readBodyLockStyle(), scrollY: window.scrollY };
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${savedBody.scrollY}px`;
      ownBody = readBodyLockStyle();
    }
    lockCount += 1;

    return () => {
      lockCount -= 1;
      if (lockCount === 0 && savedBody && ownBody) {
        const owned = ownBody;
        const untouched = LOCKED_PROPERTIES.every(
          (property) => document.body.style[property] === owned[property]
        );
        for (const property of LOCKED_PROPERTIES) {
          if (untouched) {
            document.body.style[property] = savedBody[property];
          } else if (document.body.style[property] === owned[property]) {
            document.body.style[property] = '';
          }
        }
        window.scrollTo(0, savedBody.scrollY);
        savedBody = null;
        ownBody = null;
      }
    };
  }, [active]);
};

export default useOverflowHidden;
